import { createContext, FC, ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useSocketIOContext } from "./socketIo";
import { useService } from "@/hooks/useService";
import {
  GROUP_SUBSCRIPTION_SERVICE_IDENT,
  type IGroupSubscriptionService,
} from "@bloomstack/apis/services/groupSubscription/IGroupSubscriptionService";

// ============================================================================
// Context Types
// ============================================================================

export type GroupSubscriptionContextValue = {
  /**
   * Check if currently subscribed to a group.
   */
  isSubscribed: (type: string, id: string) => boolean;
  /**
   * Request subscription to a group. Handles permission check and deduplication.
   * Returns true if subscription successful (or already subscribed).
   */
  requestSubscription: (type: string, id: string) => Promise<boolean>;
  // NOTE: No unsubscribe - socket disconnect handles cleanup
};

// ============================================================================
// Context
// ============================================================================

const GroupSubscriptionContext = createContext<GroupSubscriptionContextValue | null>(null);

/**
 * Hook to access the group subscription context.
 * Throws if used outside of GroupSubscriptionProvider.
 */
export const useGroupSubscriptionContext = (): GroupSubscriptionContextValue => {
  const context = useContext(GroupSubscriptionContext);
  if (!context) {
    throw new Error("useGroupSubscriptionContext must be used within a GroupSubscriptionProvider");
  }
  return context;
};

/**
 * Hook to optionally access the group subscription context.
 * Returns null if no provider is present.
 * Useful for components that may render with or without provider.
 */
export const useGroupSubscriptionContextOptional = (): GroupSubscriptionContextValue | null => {
  return useContext(GroupSubscriptionContext);
};

// ============================================================================
// Provider
// ============================================================================

export type GroupSubscriptionProviderProps = {
  children: ReactNode;
};

export const GroupSubscriptionProvider: FC<GroupSubscriptionProviderProps> = (props) => {
  const { children } = props;
  const socketio = useSocketIOContext();
  const groupService = useService<IGroupSubscriptionService>(GROUP_SUBSCRIPTION_SERVICE_IDENT).withPriority("high");

  // Track subscribed groups
  const [subscribed, setSubscribed] = useState<Set<string>>(new Set());
  // Keep a ref so the socket connect handler always sees the latest subscribed set
  const subscribedRef = useRef<Set<string>>(new Set());
  useEffect(() => { subscribedRef.current = subscribed; }, [subscribed]);
  // Track pending subscription requests to avoid duplicates
  const pendingRef = useRef<Set<string>>(new Set());

  const isSubscribed = useCallback((type: string, id: string): boolean => {
    return subscribed.has(`${type}:${id}`);
  }, [subscribed]);

  const requestSubscription = useCallback(async (type: string, id: string): Promise<boolean> => {
    const key = `${type}:${id}`;

    // Already subscribed
    if (subscribed.has(key)) {
      return true;
    }

    // Request already in flight
    if (pendingRef.current.has(key)) {
      return false;
    }

    // Mark as pending
    pendingRef.current.add(key);

    try {
      const result = await groupService.subscribe(type, id);
      if (result.success) {
        setSubscribed(prev => {
          const next = new Set(prev);
          next.add(key);
          return next;
        });
        return true;
      } else {
        console.warn(`[GroupSubscription] Failed to subscribe to ${key}:`, result.error);
        return false;
      }
    } catch (err) {
      console.error(`[GroupSubscription] Error subscribing to ${key}:`, err);
      return false;
    } finally {
      pendingRef.current.delete(key);
    }
  }, [subscribed, groupService]);

  // Re-subscribe all groups on reconnect (handles full disconnects where isConnected flips).
  // Gated on hasEverConnected so a fresh page load (first-ever connect) does NOT run a
  // redundant re-subscribe pass — it relies on the normal useServiceGroup → requestSubscription
  // mounting effects instead. On success we record the key back into `subscribed` (idempotent)
  // so any subscription resolved while the socket was down is reflected in state.
  useEffect(() => {
    if (!socketio.isConnected || socketio.hasEverConnected === false || subscribed.size === 0) {
      return;
    }

    for (const key of subscribed) {
      const [type, ...idParts] = key.split(':');
      const id = idParts.join(':'); // Handle IDs that might contain colons
      groupService.subscribe(type, id).then(result => {
        if (result.success) {
          setSubscribed(prev => {
            const next = new Set(prev);
            next.add(key);
            return next;
          });
        }
      }).catch(err => {
        console.error(`[GroupSubscription] Failed to re-subscribe to ${key}:`, err);
      });
    }
  }, [socketio.isConnected, socketio.hasEverConnected]);

  // Re-subscribe via direct socket connect event (handles sub-1.5s reconnects that
  // don't flip isConnected because of the debounce in SocketIOProvider). Same gate:
  // only run on genuine same-session reconnects, and record success keys into `subscribed`.
  useEffect(() => {
    const socket = socketio.socket;
    if (!socket) return;

    const handleConnect = () => {
      // First successful connect this page session is the initial mount, not a reconnect.
      if (!socketio.hasEverConnected) return;

      const currentSubscribed = subscribedRef.current;
      if (currentSubscribed.size === 0) return;
      for (const key of currentSubscribed) {
        const [type, ...idParts] = key.split(':');
        const id = idParts.join(':');
        groupService.subscribe(type, id).then(result => {
          if (result.success) {
            setSubscribed(prev => {
              const next = new Set(prev);
              next.add(key);
              return next;
            });
          }
        }).catch((err: unknown) => {
          console.error(`[GroupSubscription] Failed to re-subscribe to ${key}:`, err);
        });
      }
    };

    socket.on('connect', handleConnect);
    return () => {
      socket.off('connect', handleConnect);
    };
  }, [socketio.socket, socketio.hasEverConnected, groupService]);

  const value: GroupSubscriptionContextValue = {
    isSubscribed,
    requestSubscription,
  };

  return (
    <GroupSubscriptionContext.Provider value={value}>
      {children}
    </GroupSubscriptionContext.Provider>
  );
};
